import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { TableHead, TableWrapper } from '../components/TableWrapper';
import { formatContainerDisplayId } from '../lib/batch';
import { formatQty } from '../lib/quantity';
import { pollenApi, type PollenBatchDetails, type PollenBatchRow, type PollenMeta, type PollenPackingDetails, type PollenPackingRow } from '../services/pollen';

function formatDateOnly(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : '-';
}

export function PollenPage() {
  const toast = useToast();
  const { canWrite } = useAuth();
  const writable = canWrite('bottling');
  const currentYear = new Date().getFullYear();

  const [meta, setMeta] = useState<PollenMeta | null>(null);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [batches, setBatches] = useState<PollenBatchRow[]>([]);
  const [packings, setPackings] = useState<PollenPackingRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [harvestOpen, setHarvestOpen] = useState(false);
  const [harvestDate, setHarvestDate] = useState(new Date().toISOString().slice(0, 10));
  const [harvestApiaryIds, setHarvestApiaryIds] = useState<number[]>([]);
  const [harvestProductId, setHarvestProductId] = useState('');
  const [harvestQtyKg, setHarvestQtyKg] = useState('');
  const [harvestNotes, setHarvestNotes] = useState('');
  const [savingHarvest, setSavingHarvest] = useState(false);

  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [batchDetails, setBatchDetails] = useState<PollenBatchDetails | null>(null);
  const [loadingBatch, setLoadingBatch] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [detailStatusId, setDetailStatusId] = useState('');
  const [detailNotes, setDetailNotes] = useState('');

  const [packingOpen, setPackingOpen] = useState(false);
  const [packingDate, setPackingDate] = useState(new Date().toISOString().slice(0, 10));
  const [packingSourceId, setPackingSourceId] = useState('');
  const [packingTargetSiteId, setPackingTargetSiteId] = useState('');
  const [packingProductId, setPackingProductId] = useState('');
  const [packingQtyUnits, setPackingQtyUnits] = useState('');
  const [packingNotes, setPackingNotes] = useState('');
  const [savingPacking, setSavingPacking] = useState(false);

  const [selectedPackingId, setSelectedPackingId] = useState<number | null>(null);
  const [packingDetails, setPackingDetails] = useState<PollenPackingDetails | null>(null);
  const [loadingPacking, setLoadingPacking] = useState(false);
  const [postingPacking, setPostingPacking] = useState(false);

  const loadAll = async (year: number) => {
    setLoading(true);
    try {
      const [metaRes, batchesRes, packingsRes] = await Promise.all([
        pollenApi.meta(),
        pollenApi.batches(year),
        pollenApi.packings(year),
      ]);
      setMeta(metaRes);
      setBatches(batchesRes.batches);
      setPackings(packingsRes.packings);
      if (!harvestProductId && metaRes.bulkProducts[0]) {
        setHarvestProductId(String(metaRes.bulkProducts[0].id));
      }
      if (!packingTargetSiteId && metaRes.sites[0]) {
        setPackingTargetSiteId(String(metaRes.sites[0].id));
      }
      if (!packingProductId && metaRes.packagingProducts[0]) {
        setPackingProductId(String(metaRes.packagingProducts[0].id));
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda pollen');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadAll(selectedYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  useEffect(() => {
    if (!selectedBatchId) {
      setBatchDetails(null);
      return;
    }
    setLoadingBatch(true);
    pollenApi.batch(selectedBatchId)
      .then((res) => {
        setBatchDetails(res);
        setDetailStatusId(res.statusItemId ? String(res.statusItemId) : '');
        setDetailNotes(res.notes ?? '');
      })
      .catch((error) => {
        toast.error(error instanceof Error ? error.message : 'Kunde inte ladda batchdetalj');
        setSelectedBatchId(null);
      })
      .finally(() => setLoadingBatch(false));
  }, [selectedBatchId, toast]);

  useEffect(() => {
    if (!selectedPackingId) {
      setPackingDetails(null);
      return;
    }
    setLoadingPacking(true);
    pollenApi.packing(selectedPackingId)
      .then(setPackingDetails)
      .catch((error) => {
        toast.error(error instanceof Error ? error.message : 'Kunde inte ladda tappningsdetalj');
        setSelectedPackingId(null);
      })
      .finally(() => setLoadingPacking(false));
  }, [selectedPackingId, toast]);

  const selectedPackingProduct = useMemo(
    () => meta?.packagingProducts.find((item) => item.id === Number(packingProductId)) ?? null,
    [meta, packingProductId]
  );
  const selectedSourceContainer = useMemo(
    () => meta?.containers.find((item) => item.id === Number(packingSourceId)) ?? null,
    [meta, packingSourceId]
  );
  const packingTotalKg = useMemo(() => {
    const qty = Number(packingQtyUnits);
    const weight = Number(selectedPackingProduct?.netWeightG ?? 0);
    if (!Number.isFinite(qty) || qty <= 0 || !Number.isFinite(weight) || weight <= 0) {
      return 0;
    }
    return (qty * weight) / 1000;
  }, [packingQtyUnits, selectedPackingProduct]);

  const toggleHarvestApiary = (apiaryId: number) => {
    setHarvestApiaryIds((current) => (
      current.includes(apiaryId)
        ? current.filter((id) => id !== apiaryId)
        : [...current, apiaryId]
    ));
  };

  const saveHarvest = async () => {
    const qty = Number(harvestQtyKg.replace(',', '.'));
    if (harvestApiaryIds.length === 0 || !harvestProductId || !Number.isFinite(qty) || qty <= 0) {
      toast.error('Fyll i minst en bigård, bulkprodukt och kg');
      return;
    }
    setSavingHarvest(true);
    try {
      const created = await pollenApi.createHarvest({
        harvestDate,
        apiarySiteIds: harvestApiaryIds,
        productId: Number(harvestProductId),
        qtyKg: qty,
        notes: harvestNotes,
      });
      toast.success(`Pollenbatch skapad: ${created.batchCode}`);
      setHarvestOpen(false);
      setHarvestApiaryIds([]);
      setHarvestQtyKg('');
      setHarvestNotes('');
      await loadAll(selectedYear);
      setSelectedBatchId(created.containerId);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte spara pollenskörd');
    } finally {
      setSavingHarvest(false);
    }
  };

  const saveStatus = async () => {
    if (!selectedBatchId || !detailStatusId) {
      toast.error('Välj status');
      return;
    }
    setUpdatingStatus(true);
    try {
      await pollenApi.updateBatchStatus(selectedBatchId, { statusItemId: Number(detailStatusId), notes: detailNotes });
      toast.success('Batchstatus uppdaterad');
      const [detailRes] = await Promise.all([pollenApi.batch(selectedBatchId), loadAll(selectedYear)]);
      setBatchDetails(detailRes);
      setDetailStatusId(detailRes.statusItemId ? String(detailRes.statusItemId) : '');
      setDetailNotes(detailRes.notes ?? '');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte uppdatera status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const savePacking = async () => {
    const qtyUnits = Number(packingQtyUnits);
    if (!packingSourceId || !packingTargetSiteId || !packingProductId || !Number.isInteger(qtyUnits) || qtyUnits < 1) {
      toast.error('Välj batch, lagerplats, produkt och antal');
      return;
    }
    setSavingPacking(true);
    try {
      const created = await pollenApi.createPacking({
        packedAt: packingDate,
        sourceContainerId: Number(packingSourceId),
        targetSiteId: Number(packingTargetSiteId),
        productId: Number(packingProductId),
        qtyUnits,
        notes: packingNotes,
      });
      toast.success(`Pollen-tappning skapad: ${created.packingCode}`);
      setPackingOpen(false);
      setPackingQtyUnits('');
      setPackingNotes('');
      await loadAll(selectedYear);
      setSelectedPackingId(created.id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte skapa pollen-tappning');
    } finally {
      setSavingPacking(false);
    }
  };

  const postPacking = async () => {
    if (!packingDetails) return;
    setPostingPacking(true);
    try {
      await pollenApi.postPacking(packingDetails.id);
      toast.success('Pollen-tappning bokförd');
      const [detailRes] = await Promise.all([pollenApi.packing(packingDetails.id), loadAll(selectedYear)]);
      setPackingDetails(detailRes);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte bokföra pollen-tappning');
    } finally {
      setPostingPacking(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Pollen</h1>
          <p className="text-sm text-gray-600">Skörd, batchstatus och tappning av pollen. Blandning av pollen ligger kvar under Blandning.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setSelectedYear((value) => value - 1)}>PREV</button>
          <span className="min-w-16 text-center text-sm font-semibold">{selectedYear}</span>
          {selectedYear < currentYear && <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setSelectedYear((value) => Math.min(currentYear, value + 1))}>NEXT</button>}
          {writable && <button type="button" className="btn btn-success px-2 py-1" onClick={() => setHarvestOpen(true)}>Ny skörd</button>}
          {writable && <button type="button" className="btn btn-primary px-2 py-1" onClick={() => setPackingOpen(true)}>Ny tappning</button>}
        </div>
      </div>

      {loading || !meta ? <p className="text-sm text-gray-500">Laddar...</p> : (
        <>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-base font-semibold">Pollenbatcher</h2>
                <div className="text-sm text-gray-500">{batches.length} batcher</div>
              </div>
              <TableWrapper maxHeight="calc(100vh - 18rem)">
                <TableHead>
                  <tr>
                    <th className="px-2 py-1.5 text-left font-medium">Batch</th>
                    <th className="px-2 py-1.5 text-left font-medium">Datum</th>
                    <th className="px-2 py-1.5 text-left font-medium">Ursprung</th>
                    <th className="px-2 py-1.5 text-left font-medium">Status</th>
                    <th className="px-2 py-1.5 text-left font-medium">Kg kvar</th>
                  </tr>
                </TableHead>
                <tbody>
                  {batches.map((row) => (
                    <tr key={row.id} className="cursor-pointer border-b border-gray-100 hover:bg-orange-50" onClick={() => setSelectedBatchId(row.id)}>
                      <td className="px-2 py-1.5">
                        <div className="font-medium">{formatContainerDisplayId(row.id, row.containerCode)}</div>
                        <div className="text-xs text-gray-500">{row.productName}</div>
                      </td>
                      <td className="px-2 py-1.5">{formatDateOnly(row.batchDate)}</td>
                      <td className="px-2 py-1.5">
                        <div>{row.originLabel}</div>
                        <div className="text-xs text-gray-500">{row.apiaryName ?? row.siteName}</div>
                      </td>
                      <td className="px-2 py-1.5">
                        <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-700">
                          {row.statusLabel ?? '-'}
                        </span>
                      </td>
                      <td className="px-2 py-1.5">{formatQty(row.remainingKg, 'kg')}</td>
                    </tr>
                  ))}
                  {batches.length === 0 && <tr><td colSpan={5} className="px-2 py-4 text-sm text-gray-500">Inga pollenbatcher för {selectedYear}.</td></tr>}
                </tbody>
              </TableWrapper>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-base font-semibold">Pollen-tappningar</h2>
                <div className="text-sm text-gray-500">{packings.length} dokument</div>
              </div>
              <TableWrapper maxHeight="calc(100vh - 18rem)">
                <TableHead>
                  <tr>
                    <th className="px-2 py-1.5 text-left font-medium">Kod</th>
                    <th className="px-2 py-1.5 text-left font-medium">Datum</th>
                    <th className="px-2 py-1.5 text-left font-medium">Källa</th>
                    <th className="px-2 py-1.5 text-left font-medium">Produkt</th>
                    <th className="px-2 py-1.5 text-left font-medium">Status</th>
                  </tr>
                </TableHead>
                <tbody>
                  {packings.map((row) => (
                    <tr key={row.id} className="cursor-pointer border-b border-gray-100 hover:bg-orange-50" onClick={() => setSelectedPackingId(row.id)}>
                      <td className="px-2 py-1.5 font-medium">{row.packingCode}</td>
                      <td className="px-2 py-1.5">{formatDateOnly(row.packedAt)}</td>
                      <td className="px-2 py-1.5">{formatContainerDisplayId(row.sourceContainerId, row.sourceContainerCode)}</td>
                      <td className="px-2 py-1.5">{row.productName}</td>
                      <td className="px-2 py-1.5">{row.status === 'POSTED' ? 'Bokförd' : 'Utkast'}</td>
                    </tr>
                  ))}
                  {packings.length === 0 && <tr><td colSpan={5} className="px-2 py-4 text-sm text-gray-500">Inga pollen-tappningar för {selectedYear}.</td></tr>}
                </tbody>
              </TableWrapper>
            </div>
          </div>
        </>
      )}

      {harvestOpen && meta && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-2xl rounded border border-gray-200 bg-white p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold">Ny pollenskörd</h2>
              <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setHarvestOpen(false)}>Stäng</button>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="block text-sm">Datum<input type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD" className="mt-1 w-full" value={harvestDate} onChange={(event) => setHarvestDate(event.target.value)} /></label>
              <label className="block text-sm">Bulkprodukt<select className="mt-1 w-full" value={harvestProductId} onChange={(event) => setHarvestProductId(event.target.value)}><option value="">Välj bulkprodukt</option>{meta.bulkProducts.map((product) => <option key={product.id} value={product.id}>{product.name}</option>)}</select></label>
              <div className="text-sm md:col-span-2">
                <div className="font-medium">Bigårdar</div>
                <div className="mt-1 grid max-h-48 grid-cols-1 gap-2 overflow-y-auto rounded border border-gray-200 p-2 md:grid-cols-2">
                  {meta.apiaries.map((site) => (
                    <label key={site.id} className="flex items-center gap-2">
                      <input type="checkbox" checked={harvestApiaryIds.includes(site.id)} onChange={() => toggleHarvestApiary(site.id)} />
                      <span>{site.name}</span>
                    </label>
                  ))}
                  {meta.apiaries.length === 0 && <div className="text-gray-500">Inga aktiva bigårdar finns.</div>}
                </div>
              </div>
              <label className="block text-sm md:col-span-2">Kg<input className="mt-1 w-full" value={harvestQtyKg} onChange={(event) => setHarvestQtyKg(event.target.value)} placeholder="t.ex. 4,5" /></label>
            </div>
            <div className="rounded border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-900">Ny batch får automatiskt statusen <span className="font-semibold">Nyskördad</span>, batchkod i serien <span className="font-semibold">Pyy.n</span> och läggs på lagret <span className="font-semibold">Lager</span>.</div>
            <label className="block text-sm">Notering<textarea className="mt-1 min-h-[100px] w-full" value={harvestNotes} onChange={(event) => setHarvestNotes(event.target.value)} placeholder="Typ av pollen, t.ex. maskrospollen" /></label>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setHarvestOpen(false)}>Avbryt</button>
              <button type="button" className="btn btn-success px-2 py-1" disabled={savingHarvest} onClick={saveHarvest}>{savingHarvest ? 'Sparar...' : 'Spara skörd'}</button>
            </div>
          </div>
        </div>
      )}

      {selectedBatchId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-3xl rounded border border-gray-200 bg-white p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold">{formatContainerDisplayId(batchDetails?.id ?? selectedBatchId, batchDetails?.containerCode) || `Batch ${selectedBatchId}`}</h2>
              <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setSelectedBatchId(null)}>Stäng</button>
            </div>
            {loadingBatch && <p className="text-sm text-gray-500">Laddar batchdetalj...</p>}
            {batchDetails && !loadingBatch && (
              <>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="rounded border border-gray-200 p-3 text-sm">
                    <div><span className="font-medium">Produkt:</span> {batchDetails.productName}</div>
                    <div><span className="font-medium">Datum:</span> {formatDateOnly(batchDetails.batchDate)}</div>
                    <div><span className="font-medium">Ursprung:</span> {batchDetails.originLabel}</div>
                    <div><span className="font-medium">Bigårdar:</span> {batchDetails.apiaryName ?? '-'}</div>
                    <div><span className="font-medium">Lagerplats:</span> {batchDetails.siteName}</div>
                    <div><span className="font-medium">Kg kvar:</span> {formatQty(batchDetails.remainingKg, 'kg')}</div>
                  </div>
                  <div className="rounded border border-gray-200 p-3 text-sm">
                    <div className="font-medium">Status och notering</div>
                    <select className="mt-2 w-full" value={detailStatusId} onChange={(event) => setDetailStatusId(event.target.value)} disabled={!writable}>
                      <option value="">Välj status</option>
                      {meta?.statuses.map((status) => <option key={status.id} value={status.id}>{status.label}</option>)}
                    </select>
                    <textarea className="mt-2 min-h-[100px] w-full" value={detailNotes} onChange={(event) => setDetailNotes(event.target.value)} disabled={!writable} />
                    {writable && (
                      <div className="mt-2 flex justify-end">
                        <button type="button" className="btn btn-success px-2 py-1" disabled={updatingStatus} onClick={saveStatus}>
                          {updatingStatus ? 'Sparar...' : 'Spara status'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {batchDetails.notes && (
                  <div className="rounded border border-gray-200 p-3 text-sm">
                    <div className="font-medium">Anteckning</div>
                    <p className="mt-1 text-gray-700">{batchDetails.notes}</p>
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium">Tappningar från batchen</div>
                    {writable && (
                      <button type="button" className="btn btn-primary px-2 py-1" onClick={() => {
                        setPackingSourceId(String(batchDetails.id));
                        setPackingOpen(true);
                      }}>
                        Ny tappning från batch
                      </button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {batchDetails.packings.map((packing) => (
                      <button
                        key={packing.id}
                        type="button"
                        className="w-full rounded border border-gray-200 px-3 py-2 text-left text-sm hover:bg-gray-50"
                        onClick={() => setSelectedPackingId(packing.id)}
                      >
                        <div className="font-medium">{packing.packingCode}</div>
                        <div className="text-gray-600">{formatDateOnly(packing.packedAt)} • {packing.productName} • {packing.qtyUnits} st • {packing.status === 'POSTED' ? 'Bokförd' : 'Utkast'}</div>
                      </button>
                    ))}
                    {batchDetails.packings.length === 0 && <div className="rounded border border-dashed border-gray-300 px-3 py-4 text-sm text-gray-500">Ingen pollen-tappning har ännu skapats från batchen.</div>}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {packingOpen && meta && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-2xl rounded border border-gray-200 bg-white p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold">Ny pollen-tappning</h2>
              <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setPackingOpen(false)}>Stäng</button>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="block text-sm">Datum<input type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD" className="mt-1 w-full" value={packingDate} onChange={(event) => setPackingDate(event.target.value)} /></label>
              <label className="block text-sm">Pollenbatch<select className="mt-1 w-full" value={packingSourceId} onChange={(event) => setPackingSourceId(event.target.value)}><option value="">Välj batch</option>{meta.containers.map((container) => <option key={container.id} value={container.id}>{formatContainerDisplayId(container.id, container.containerCode)} • {container.productName} • {formatQty(container.remainingKg, 'kg')} kg kvar</option>)}</select></label>
              <label className="block text-sm">Lagerplats<select className="mt-1 w-full" value={packingTargetSiteId} onChange={(event) => setPackingTargetSiteId(event.target.value)}><option value="">Välj plats</option>{meta.sites.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}</select></label>
              <label className="block text-sm">Färdig pollenprodukt<select className="mt-1 w-full" value={packingProductId} onChange={(event) => setPackingProductId(event.target.value)}><option value="">Välj färdig produkt</option>{meta.packagingProducts.map((product) => <option key={product.id} value={product.id}>{product.name}</option>)}</select></label>
              <label className="block text-sm md:col-span-2">Antal<input type="number" min={1} className="mt-1 w-full" value={packingQtyUnits} onChange={(event) => setPackingQtyUnits(event.target.value)} /></label>
            </div>
            <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              Total mängd: <span className="font-semibold">{formatQty(packingTotalKg, 'kg')} kg</span>
              {selectedSourceContainer && <span className="ml-2">• Batch {formatContainerDisplayId(selectedSourceContainer.id, selectedSourceContainer.containerCode)} har {formatQty(selectedSourceContainer.remainingKg, 'kg')} kg kvar</span>}
            </div>
            <label className="block text-sm">Anteckning<textarea className="mt-1 min-h-[90px] w-full" value={packingNotes} onChange={(event) => setPackingNotes(event.target.value)} /></label>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setPackingOpen(false)}>Avbryt</button>
              <button type="button" className="btn btn-success px-2 py-1" disabled={savingPacking} onClick={savePacking}>{savingPacking ? 'Sparar...' : 'Skapa tappning'}</button>
            </div>
          </div>
        </div>
      )}

      {selectedPackingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-2xl rounded border border-gray-200 bg-white p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold">{packingDetails?.packingCode ?? `Tappning ${selectedPackingId}`}</h2>
              <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setSelectedPackingId(null)}>Stäng</button>
            </div>
            {loadingPacking && <p className="text-sm text-gray-500">Laddar tappningsdetalj...</p>}
            {packingDetails && !loadingPacking && (
              <>
                <div className="text-sm text-gray-700">
                  <div>Datum: {formatDateOnly(packingDetails.packedAt)}</div>
                  <div>Källbatch: {formatContainerDisplayId(packingDetails.sourceContainerId, packingDetails.sourceContainerCode)}</div>
                  <div>Källplats: {packingDetails.sourceSiteName}</div>
                  <div>Målplats: {packingDetails.targetSiteName}</div>
                  <div>Status: {packingDetails.status === 'POSTED' ? 'Bokförd' : 'Utkast'}</div>
                </div>
                {packingDetails.notes && <p className="text-sm text-gray-700">{packingDetails.notes}</p>}
                <div className="space-y-2">
                  {packingDetails.lines.map((line) => (
                    <div key={line.id} className="rounded border border-gray-200 px-3 py-2 text-sm">
                      <div className="font-medium">{line.productName}</div>
                      <div className="text-gray-600">{line.qtyUnits} st • {formatQty(line.unitWeightG, 'g')} g • {formatQty(line.totalKg, 'kg')} kg • Batch {line.batchCode}</div>
                    </div>
                  ))}
                </div>
                {writable && packingDetails.status !== 'POSTED' && (
                  <div className="flex justify-end">
                    <button type="button" className="btn btn-success px-2 py-1" disabled={postingPacking} onClick={postPacking}>
                      {postingPacking ? 'Bokför...' : 'Bokför tappning'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
